import * as React from 'react';
import { styled } from '@linaria/react';
import { Play } from 'lucide-react';

import { roundTo } from '@/utils';

import ColorModeContainer from '@/components/ColorModeContainer';
import ButtonPrimary from '@/components/ButtonPlayful/ButtonPrimary';
import { BaseWrapper as AsideWrapper } from '@/components/Aside';

import AnimatedBlock, {
  CONTAINER_WIDTH,
  DISPLACEMENT,
} from '../AnimatedBlock';
import LinearGraph from './LinearGraph';

// Each point can either be a number of Y-axis values from 0-100 (and beyond), or it can be a string with Y/X pairs ("50 25%").
// This was kind of a weird choice on my part, since the actual values need to be normalized to 0-1. I do that within this function to come up with `linearString`.
type Point = number | string;

interface Props {
  initialPoints: Array<Point>;
  headroom?: number;
  duration?: number;
  includeHoverTooltip?: boolean;
  justTheGraph?: boolean;
  drawingEnabled?: boolean;
}

function LinearDemo({
  initialPoints,
  headroom = 0,
  duration = 500,
  justTheGraph = false,
  drawingEnabled = false,
  includeHoverTooltip = false,
}: Props) {
  const [points, setPoints] = React.useState<Array<Point>>(() =>
    initialPoints.map((p) =>
      typeof p === 'number' ? roundTo(p, 2) : p
    )
  );

  const [side, setSide] = React.useState<'left' | 'right'>('left');

  const linearString = `linear(${points
    .map((p) => {
      // Our points use a 0-100 scale, but linear uses 0-1.
      // When working with basic numbers, this is easy:
      if (typeof p === 'number') {
        return p / 100;
      } else {
        // For strings, we need to rip it apart to make our adjustment:
        const [y, x] = p.split(' ');
        return `${Number(y) / 100} ${x}`;
      }
    })
    .join(',')})`;

  const style = {
    transform: `translateX(${side === 'left' ? 0 : DISPLACEMENT}px)`,
    transition: `transform ${duration}ms ${linearString}`,
  };

  return (
    <Wrapper colorMode="dark">
      <StyledLinearGraph
        points={points}
        setPoints={setPoints}
        headroom={headroom}
        drawingEnabled={drawingEnabled}
        includeHoverTooltip={includeHoverTooltip}
      />

      {!justTheGraph && (
        <>
          <BlockWrapper>
            <AnimatedBlock style={style} />
          </BlockWrapper>
          <HorizontalRule />
          <ActionButton
            Icon={Play}
            onClick={() =>
              setSide(side === 'left' ? 'right' : 'left')
            }
          >
            Run Animation
          </ActionButton>
        </>
      )}
    </Wrapper>
  );
}

const Wrapper = styled(ColorModeContainer)`
  width: fit-content;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 16px auto 64px;
  padding: 1rem;
  border-radius: 8px;
  border: var(--page-border);

  @media (max-width: 25rem) {
    width: calc(100% + var(--viewport-padding) * 2);
    max-width: revert;
    margin-inline: calc(var(--viewport-padding) * -1);
    overflow: hidden;
    margin-bottom: 48px;
  }

  ${AsideWrapper} && {
    border: none;
    margin-bottom: 32px;
  }
`;

const StyledLinearGraph = styled(LinearGraph)`
  width: ${CONTAINER_WIDTH}px;
  max-width: 100%;
`;

const HorizontalRule = styled.div`
  width: calc(100% + 2rem);
  margin: 36px -1rem 1rem;
  height: 1px;
  background: var(--color-gray-200);
`;

const BlockWrapper = styled.div`
  margin-top: 16px;
`;

const ActionButton = styled(ButtonPrimary)`
  width: ${CONTAINER_WIDTH}px;
  max-width: 100%;
`;

export default LinearDemo;
